import React, { Dispatch, SetStateAction, useMemo, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Header } from "antd/lib/layout/layout";
import { CheckCircleFilled, CloseCircleFilled, DownOutlined, HistoryOutlined, SyncOutlined } from "@ant-design/icons";
import { PAGE_TITLE } from "../../../utils";
import { LogoutIcon, MenuIcon } from "../../../assets/icons";
import { useAuthUser } from "../../../hooks";
import "./app-header.scss";
import { BackArrow } from "../../../components";
import { logoutUser } from "../../../store";

const formatVersionDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
  const day = date.getDate();
  const year = date.getFullYear();
  const time = date.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${month} ${day}, ${year} • ${time}`;
};

const statusBadgeMap: Record<string, string> = {
  READY: "version-status-badge--success",
  PROCESSING: "version-status-badge--processing",
  FAILED: "version-status-badge--error",
  QUEUED: "version-status-badge--queued",
};

const getStatusConfig = (status: string) => {
  const normalized = status.toUpperCase();
  return {
    label: normalized,
    className: statusBadgeMap[normalized] ?? "version-status-badge--queued",
  };
};

const statusIconMap: Record<string, React.ReactNode> = {
  READY: <CheckCircleFilled className="version-status-icon version-status-icon--success" />,
  FAILED: <CloseCircleFilled className="version-status-icon version-status-icon--error" />,
  PROCESSING: <SyncOutlined spin className="version-status-icon version-status-icon--processing" />,
};

const getStatusIcon = (status: string) => {
  return statusIconMap[status.toUpperCase()] ?? <SyncOutlined className="version-status-icon version-status-icon--queued" />;
};

const getVersionNumber = (version: string) => {
  const match = version.match(/\d+/);
  return match ? match[0] : version;
};

export const AppHeader = ({ pageHeader }: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { firstName, lastName, roleName, keycloak } = useAuthUser();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const versionInfo = pageHeader?.versionInfo;
  const showVersionDropdown =
    location.pathname.includes("/document/") &&
    versionInfo &&
    versionInfo.availableVersions.length > 1;

  const currentVersionData = versionInfo?.availableVersions.find(
    (v) => v.version === versionInfo.selectedVersion
  );

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const { pageTitle, displayBackButton, pageRoute } = useMemo(() => {
    if (pageHeader?.pageTitle) {
      return {
        pageTitle: pageHeader.pageTitle,
        displayBackButton: pageHeader.displayBackButton,
        pageRoute: pageHeader.displayBackButton,
      };
    }
    const pathname = location.pathname;
    let title = PAGE_TITLE[0].title;
    let backButton = false;
    let pageURL = "";
    PAGE_TITLE.forEach((page) => {
      if (pathname.includes(page.route)) {
        title = page.title;
        backButton = page.displayBackButton;
        pageURL = page.route;
      }
    });
    return { pageTitle: title, displayBackButton: backButton, pageRoute: pageURL };
  }, [location.pathname, pageHeader?.pageTitle]);

  const logout = async (e: any) => {
    try {
      e.preventDefault();
      localStorage.clear();
      if (!keycloak.idToken) {
        throw new Error("ID Token is missing. Cannot log out.");
      }
      const keycloakLogoutUrl =
        `${import.meta.env.VITE_APP_KEYCLOAK_BASE_URL}realms/${keycloak.realm}/protocol/openid-connect/logout` +
        "?id_token_hint=" +
        keycloak.idToken +
        "&post_logout_redirect_uri=" +
        encodeURIComponent(window.location.origin);
      window.location.replace(keycloakLogoutUrl);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleVersionSelect = (version: string) => {
    versionInfo?.switchVersion(version);
    setDropdownOpen(false);
  };

  return (
    <Header className="flex aic jcsb header">
      <div className="flex gp aic">
        {displayBackButton && <BackArrow />}
        <h1 className="header-h1 m-0">{pageTitle}</h1>
        {showVersionDropdown && <span className="header-page-divider" />}
        {showVersionDropdown && (
          <div className="version-dropdown-wrapper" ref={dropdownRef}>
            <button
              type="button"
              className="header-version-pill"
              onClick={() => setDropdownOpen((prev) => !prev)}
            >
              <HistoryOutlined className="version-left-icon" />
              <span className="version-text">
                Version {getVersionNumber(versionInfo.selectedVersion)}
              </span>
              <span className="version-divider" />
              {currentVersionData && getStatusIcon(currentVersionData.status)}
              <DownOutlined className={`version-down-icon ${dropdownOpen ? "version-down-icon--open" : ""}`} />
            </button>
            {dropdownOpen && (
              <div className="version-dropdown-panel">
                <div className="version-dropdown-header">DOCUMENT HISTORY</div>
                <div className="version-dropdown-list">
                  {[...versionInfo.availableVersions]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((v) => {
                      const isSelected = v.version === versionInfo.selectedVersion;
                      const statusConfig = getStatusConfig(v.status);
                      return (
                        <button
                          key={v.version}
                          type="button"
                          className={`version-dropdown-item ${isSelected ? "version-dropdown-item--active" : ""}`}
                          onClick={() => handleVersionSelect(v.version)}
                        >
                          <div className="version-dropdown-item-top">
                            <span className="version-dropdown-item-label">
                              Version {getVersionNumber(v.version)}
                            </span>
                            <span className={`version-status-badge ${statusConfig.className}`}>
                              {statusConfig.label}
                            </span>
                          </div>
                          <span className="version-dropdown-item-date">
                            {formatVersionDate(v.createdAt)}
                          </span>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="header-right flex gp-10 aic">
        <div className="header-content-menu pr cursor-pointer">
          <a className="header-dropdown" href="#">
            <MenuIcon />
          </a>
          <div className="header-dropdown-menu flex flex-column">
            <div className="header-content-profile-content">
              <p>
                {firstName} {lastName} <span>{roleName}</span>
              </p>
            </div>
            <a href="#" className="header-dropdown-menu-item flex aic no-wrap" onClick={logout}>
              <LogoutIcon />
              Logout
            </a>
          </div>
        </div>
      </div>
    </Header>
  );
};

interface Props {
  pageHeader?: PageHeader;
  setPageHeader?: Dispatch<SetStateAction<PageHeader>>;
}

export interface PageHeader {
  pageTitle: string;
  displayBackButton: boolean;
  pageRoute: string;
  versionInfo?: {
    selectedVersion: string;
    availableVersions: { version: string; status: string; createdAt: string }[];
    switchVersion: (version: string) => void;
  };
}
